import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Brain } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LanguageContext";
import { PageHeader, PrioriteBadge, StatutBadge, DelaiBadge } from "@/components/shared";
import { CREATE_CLINICAL } from "@/lib/rbac";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { DictationButton } from "@/components/DictationButton";
import { PrescripteurSelect, usePrescripteurs } from "@/components/PrescripteurSelect";
import { RequeteFields } from "@/components/RequeteFields";
import { ScanRequeteButton } from "@/components/ScanRequeteButton";
import { downloadFile } from "@/lib/api";
import { LotPanel, loadSaved } from "@/components/LotPanel";
import { printEtiquettes } from "@/components/EtiquettesButtons";
import { Checkbox } from "@/components/ui/checkbox";
import { Card } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

export default function Demandes() {
  const { user } = useAuth();
  const { t } = useLang();
  const navigate = useNavigate();
  const [demandes, setDemandes] = useState([]);
  const [patients, setPatients] = useState([]);
  const [open, setOpen] = useState(false);
  const EMPTY = { patient_id: "", type_examen: "", nature_specimen: "", priorite: "normal", prescripteur: "", prescripteur_courriel: "", prescripteur_id: null, clinique: "", renseignements_cliniques: "" };
  const [form, setForm] = useState(EMPTY);
  const [lot, setLot] = useState([]);
  const [showLot, setShowLot] = useState(false);
  const [lotIdx, setLotIdx] = useState(null);
  const [creerPrel, setCreerPrel] = useState(true);
  const [sortIa, setSortIa] = useState(false);
  const canCreate = CREATE_CLINICAL.includes(user?.role);
  const prescripteurs = usePrescripteurs(canCreate);
  const pickPresc = (c) => setForm((f) => ({ ...f, prescripteur_id: c?.id || null, prescripteur: c ? c.nom : "", prescripteur_courriel: c ? c.email : "" }));
  const rows = sortIa ? [...demandes].sort((a, b) => (b.ia_score ?? -1) - (a.ia_score ?? -1)) : demandes;
  const [requete, setRequete] = useState({});
  const [scan, setScan] = useState(null);
  const norm = (s) => (s || "").replace(/[^A-Z0-9]/gi, "").toUpperCase();
  const applyScan = (data, preview) => {
    const c = data.champs || {};
    setScan({ preview, incertitudes: c.incertitudes || [], patient: c.patient || {} });
    const { patient, incertitudes, prescripteur, renseignements_cliniques, ...req } = c;
    setRequete({ ...req, scan_id: data.scan_id });
    const cond = c.conditions || [];
    const pat = c.patient?.ramq && patients.find((p) => norm(p.ramq) === norm(c.patient.ramq));
    const acct = prescripteur && prescripteurs.find((p) => norm(p.nom).replace(/^DRE?/, "") === norm(prescripteur).replace(/^DRE?/, ""));
    setForm((f) => ({
      ...f,
      patient_id: pat ? pat.id : f.patient_id,
      type_examen: f.type_examen || c.operation || (c.sites?.A ? `Biopsie — ${c.sites.A}` : ""),
      priorite: cond.includes("extemporane") || cond.includes("etat_frais") ? "stat" : cond.includes("urgent") ? "urgent" : f.priorite,
      renseignements_cliniques: f.renseignements_cliniques || renseignements_cliniques || "",
      ...(acct ? { prescripteur_id: acct.id, prescripteur: acct.nom, prescripteur_courriel: acct.email } : prescripteur && !f.prescripteur_id ? { prescripteur } : {}),
    }));
    if (c.patient?.ramq && !pat) toast.warning("Patient non trouvé par son RAMQ : choisissez-le ou créez sa fiche");
  };
  const validateLot = (i, items = lot) => {
    const it = items[i];
    setForm(EMPTY); setRequete({}); setScan(null); setLotIdx(i);
    applyScan(it.data, it.preview);
    setOpen(true);
  };
  const nextLot = (items) => {
    const n = items.findIndex((x) => x.status === "prete");
    if (n >= 0) setTimeout(() => validateLot(n, items), 300);
  };

  const openIa = (d, e) => {
    e.stopPropagation();
    if (d.ia_lame_id) navigate(`/wsi/${d.ia_lame_id}`);
    else if (d.ia_analyse_id) navigate(`/analyses/${d.ia_analyse_id}`);
  };

  const load = () => api.get("/demandes").then((r) => setDemandes(r.data)).catch(() => {});
  useEffect(() => {
    load();
    if (canCreate) api.get("/patients").then((r) => setPatients(r.data)).catch(() => {});
    if (canCreate) loadSaved(setLot).catch(() => {});
  }, [canCreate]);

  const submit = async () => {
    if (!form.patient_id || !form.type_examen) return toast.error(t("dm_req_err"));
    const nbSites = Object.keys(requete.sites || {}).length;
    const payload = { ...form, requete: Object.keys(requete).length ? requete : null, creer_prelevements: creerPrel && nbSites > 0 };
    if (!payload.nature_specimen || payload.nature_specimen === "auto") delete payload.nature_specimen;
    try {
      const { data: created } = await api.post("/demandes", payload);
      if (payload.creer_prelevements) {
        toast.success(`${t("dm_created")} · ${nbSites} prélèvement(s) créé(s)`, {
          duration: 12000,
          action: { label: "Imprimer les étiquettes", onClick: () => printEtiquettes(created, localStorage.getItem("etiquettes_format") || "rouleau") },
        });
      } else toast.success(t("dm_created"));
      setOpen(false); setRequete({}); setScan(null); setForm(EMPTY); load();
      if (lotIdx !== null) {
        const items = lot.map((x, j) => (j === lotIdx ? { ...x, status: "validee" } : x));
        setLot(items); setLotIdx(null); nextLot(items);
      }
    } catch (e) {
      if (e.response?.status === 409 && lotIdx !== null) {
        const items = lot.map((x, j) => (j === lotIdx ? { ...x, status: "validee", erreur: "Déjà validée sur un autre poste" } : x));
        setLot(items); setLotIdx(null); setOpen(false); nextLot(items);
      }
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  return (
    <div data-testid="demandes-page">
      <PageHeader title={t("dm_title")} subtitle={t("dm_subtitle")}>
        {canCreate && (
          <Button variant="outline" onClick={() => setShowLot((s) => !s)} data-testid="lot-toggle-button">
            Numérisation en lot{lot.filter((x) => x.status === "prete").length ? ` (${lot.filter((x) => x.status === "prete").length})` : ""}
          </Button>
        )}
        {canCreate && (
          <Button variant="outline" onClick={() => downloadFile("/requetes/vierge/pdf", "requete_anatomo_pathologie.pdf").catch(() => toast.error("Téléchargement impossible"))} data-testid="requete-vierge-button">
            Requête vierge (PDF)
          </Button>
        )}
        {canCreate && (
          <Button variant="outline" onClick={() => navigate("/demandes/relier-prescripteurs")} data-testid="relier-prescripteurs-button">
            Relier les anciens dossiers
          </Button>
        )}
        {canCreate && (
          <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) setLotIdx(null); }}>
            <DialogTrigger asChild>
              <Button data-testid="new-demande-button" style={{ background: "#0F2942" }}>
                <Plus className="h-4 w-4 mr-1" /> {t("dm_new")}
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-white max-w-3xl max-h-[90vh] overflow-y-auto" aria-describedby="new-demande-desc">
              <DialogHeader>
                <DialogTitle>{t("dm_new")}{lotIdx !== null ? ` — requête ${lotIdx + 1} / ${lot.length} du lot` : ""}</DialogTitle>
                <DialogDescription id="new-demande-desc">{t("dm_new_desc")}</DialogDescription>
              </DialogHeader>
              <div className="space-y-3">
                <ScanRequeteButton onResult={applyScan} />
                {scan && (
                  <div className="flex gap-3 rounded-lg border border-amber-200 bg-amber-50 p-2 text-xs text-amber-900" data-testid="scan-requete-result">
                    <img src={scan.preview} alt="Requête" className="h-24 rounded border object-cover" />
                    <div>
                      <div className="font-semibold">Lu par l'IA — vérifiez avant d'enregistrer. L'image sera jointe à la demande.</div>
                      {scan.patient?.nom && <div>Patient lu : {scan.patient.nom} {scan.patient.prenom || ""} {scan.patient.ramq ? `· ${scan.patient.ramq}` : ""}</div>}
                      {scan.incertitudes.length > 0 && <div data-testid="scan-requete-incertitudes">Lecture douteuse (encadré orange) : {scan.incertitudes.join(", ")}</div>}
                    </div>
                  </div>
                )}
                <div><Label>{t("dm_patient")}</Label>
                  <Select value={form.patient_id} onValueChange={(v) => setForm({ ...form, patient_id: v })}>
                    <SelectTrigger data-testid="demande-patient-select"><SelectValue placeholder={t("dm_patient_ph")} /></SelectTrigger>
                    <SelectContent className="bg-white">
                      {patients.map((p) => <SelectItem key={p.id} value={p.id}>{p.nom} {p.prenom} — {p.ramq}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label>{t("dm_type")}</Label><Input data-testid="demande-type-input" value={form.type_examen} onChange={(e) => setForm({ ...form, type_examen: e.target.value })} placeholder={t("dm_type_ph")} /></div>
                  <div><Label>{t("dm_nature")}</Label>
                    <Select value={form.nature_specimen} onValueChange={(v) => setForm({ ...form, nature_specimen: v })}>
                      <SelectTrigger data-testid="demande-nature-select"><SelectValue placeholder={t("dm_nature_auto_ph")} /></SelectTrigger>
                      <SelectContent className="bg-white">
                        <SelectItem value="auto">{t("dm_nature_auto")}</SelectItem>
                        <SelectItem value="biopsie">{t("dm_nature_biopsie")}</SelectItem>
                        <SelectItem value="piece_chirurgicale">{t("dm_nature_piece")}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div><Label>{t("dm_priorite")}</Label>
                    <Select value={form.priorite} onValueChange={(v) => setForm({ ...form, priorite: v })}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent className="bg-white">
                        <SelectItem value="normal">{t("priorite_normal")}</SelectItem>
                        <SelectItem value="urgent">{t("priorite_urgent")}</SelectItem>
                        <SelectItem value="stat">{t("priorite_stat")}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="col-span-2"><Label>Compte du médecin prescripteur</Label><PrescripteurSelect list={prescripteurs} value={form.prescripteur_id} onChange={pickPresc} testId="demande-prescripteur-select" /></div>
                  <div><Label>{t("dm_prescripteur")}</Label><Input disabled={!!form.prescripteur_id} data-testid="demande-prescripteur-input" value={form.prescripteur} onChange={(e) => setForm({ ...form, prescripteur: e.target.value })} /></div>
                  <div><Label>{t("dm_courriel")}</Label><Input type="email" disabled={!!form.prescripteur_id} data-testid="demande-prescripteur-courriel-input" value={form.prescripteur_courriel} onChange={(e) => setForm({ ...form, prescripteur_courriel: e.target.value })} placeholder={t("dm_courriel_ph")} /></div>
                  <div><Label>{t("dm_clinique")}</Label><Input value={form.clinique} onChange={(e) => setForm({ ...form, clinique: e.target.value })} /></div>
                </div>
                <div><Label>{t("dm_renseignements")}</Label><Textarea data-testid="demande-renseignements-textarea" value={form.renseignements_cliniques} onChange={(e) => setForm({ ...form, renseignements_cliniques: e.target.value })} /><DictationButton value={form.renseignements_cliniques || ""} onChange={(v) => setForm((f) => ({ ...f, renseignements_cliniques: v }))} testId="dictation-renseignements" /></div>
                <RequeteFields value={requete} incertitudes={scan?.incertitudes} onChange={(r) => {
                  setRequete(r);
                  const c = r.conditions || [];
                  setForm((f) => ({ ...f, priorite: c.includes("extemporane") || c.includes("etat_frais") ? "stat" : c.includes("urgent") ? "urgent" : "normal" }));
                }} />
                {Object.keys(requete.sites || {}).length > 0 && (
                  <label className="flex items-center gap-2 text-sm cursor-pointer rounded-lg bg-sky-50 px-3 py-2" data-testid="creer-prelevements-label">
                    <Checkbox checked={creerPrel} onCheckedChange={(v) => setCreerPrel(!!v)} data-testid="creer-prelevements-checkbox" />
                    Créer un prélèvement par site rempli ({Object.keys(requete.sites).sort().join(", ")})
                  </label>
                )}
              </div>
              <DialogFooter>
                <Button onClick={submit} data-testid="demande-save-button" style={{ background: "#0284C7" }}>{t("dm_create")}</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </PageHeader>
      {canCreate && (showLot || lot.length > 0) && <LotPanel lot={lot} setLot={setLot} onValidate={(i) => validateLot(i)} />}

      <div className="flex justify-end mb-2">
        <button onClick={() => setSortIa((s) => !s)} data-testid="demandes-sort-ia"
                className={`text-xs px-3 py-1.5 rounded-lg border inline-flex items-center gap-1.5 transition-colors ${sortIa ? "bg-violet-600 text-white border-violet-600" : "border-slate-200 text-slate-600 hover:bg-slate-50"}`}>
          <Brain className="h-3.5 w-3.5" /> {t("dm_sort_ia")}{sortIa ? " ↓" : ""}
        </button>
      </div>

      <Card className="border-slate-200 hidden md:block">
        <Table className="clin-table">
          <TableHeader>
            <TableRow>
              <TableHead>{t("dm_col_num")}</TableHead><TableHead>{t("dm_col_patient")}</TableHead>
              <TableHead>{t("dm_col_type")}</TableHead><TableHead>{t("dm_col_priorite")}</TableHead>
              <TableHead>{t("dm_col_statut")}</TableHead><TableHead>{t("dm_col_delai")}</TableHead><TableHead>{t("dm_col_ia")}</TableHead><TableHead>{t("dm_col_presc")}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody data-testid="demandes-table-body">
            {rows.map((d) => (
              <TableRow key={d.id} className="h-9 cursor-pointer" onClick={() => navigate(`/demandes/${d.id}`)}
                        data-testid={`demande-row-${d.numero}`}>
                <TableCell className="font-mono text-xs font-semibold text-sky-700">{d.numero}</TableCell>
                <TableCell>{d.patient ? `${d.patient.nom} ${d.patient.prenom}` : "—"}</TableCell>
                <TableCell>{d.type_examen}</TableCell>
                <TableCell><PrioriteBadge value={d.priorite} /></TableCell>
                <TableCell><StatutBadge value={d.statut} /></TableCell>
                <TableCell><DelaiBadge value={d.delai} cible={d.cible_label} /></TableCell>
                <TableCell>
                  {d.ia_disponible ? (
                    <button data-testid={`demande-ia-badge-${d.numero}`} onClick={(e) => openIa(d, e)}
                            title={t("dm_open_slide")}
                            className={`text-[10px] font-semibold px-2 py-0.5 rounded-full inline-flex items-center gap-1 text-white hover:brightness-110 ${d.ia_score >= 0.7 ? "bg-red-600" : d.ia_score >= 0.4 ? "bg-amber-500" : "bg-teal-600"}`}>
                      <Brain className="h-3 w-3" /> {Math.round(d.ia_score * 100)}%
                    </button>
                  ) : <span className="text-slate-300 text-xs">—</span>}
                </TableCell>
                <TableCell>{d.prescripteur || "—"}</TableCell>
              </TableRow>
            ))}
            {demandes.length === 0 && (
              <TableRow><TableCell colSpan={8} className="text-center text-slate-400 py-8">{t("dm_empty")}</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </Card>

      {/* Vue cartes — mobile */}
      <div className="md:hidden space-y-3" data-testid="demandes-cards">
        {rows.map((d) => (
          <div key={d.id} onClick={() => navigate(`/demandes/${d.id}`)}
               data-testid={`demande-card-${d.numero}`}
               className="bg-white border border-slate-200 rounded-xl p-4 cursor-pointer active:bg-slate-50 transition-colors">
            <div className="flex items-center justify-between gap-2">
              <span className="font-mono text-xs font-semibold text-sky-700">{d.numero}</span>
              <div className="flex items-center gap-1.5">
                <PrioriteBadge value={d.priorite} />
                {d.ia_disponible && (
                  <button data-testid={`demande-ia-badge-m-${d.numero}`} onClick={(e) => openIa(d, e)}
                          className={`text-[10px] font-semibold px-2 py-0.5 rounded-full inline-flex items-center gap-1 text-white ${d.ia_score >= 0.7 ? "bg-red-600" : d.ia_score >= 0.4 ? "bg-amber-500" : "bg-teal-600"}`}>
                    <Brain className="h-3 w-3" /> {Math.round(d.ia_score * 100)}%
                  </button>
                )}
              </div>
            </div>
            <div className="mt-1.5 text-sm font-medium text-slate-800">{d.patient ? `${d.patient.nom} ${d.patient.prenom}` : "—"}</div>
            <div className="text-xs text-slate-500">{d.type_examen}</div>
            <div className="mt-2 flex items-center gap-2 flex-wrap">
              <StatutBadge value={d.statut} />
              <DelaiBadge value={d.delai} cible={d.cible_label} />
            </div>
            {d.prescripteur && <div className="mt-2 text-[11px] text-slate-400">{t("dm_col_presc")} : {d.prescripteur}</div>}
          </div>
        ))}
        {demandes.length === 0 && (
          <div className="text-center text-slate-400 py-8 bg-white border border-slate-200 rounded-xl">{t("dm_empty")}</div>
        )}
      </div>
    </div>
  );
}
